import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { Search, MapPin, Briefcase } from 'lucide-react';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg } from '../finance/shared';
import CoachProfileModal from './CoachProfileModal';

const COACH_TYPES = { head_coach: 'مدرب رئيسي', assistant_coach: 'مدرب مساعد', goalkeeper_coach: 'مدرب حراس مرمى', fitness_coach: 'مدرب لياقة', conditioning_coach: 'مدرب إعداد بدني', performance_analyst: 'محلل أداء', youth_coach: 'مدرب فئات سنية', other: 'تخصص آخر' };

export default function CoachSearchTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', city: '', specialty: '' });
  const [requestModal, setRequestModal] = useState(null);
  const [reqForm, setReqForm] = useState({ position_title: '', message: '' });
  const [profileCoachId, setProfileCoachId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/coaches/search`, { ...auth(), params });
      setItems(res.data.items); setTotal(res.data.total);
    } catch { setItems([]); setTotal(0); }
    setLoading(false);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const saveCandidate = async (coachId) => {
    try { await axios.post(`${API}/coaches/candidates`, { coach_id: coachId }, auth()); toast.success('تمت إضافته لقائمة المرشحين'); }
    catch (err) { toast.error(errMsg(err, 'فشلت الإضافة')); }
  };

  const sendRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/coaches/requests`, { coach_id: requestModal.id, ...reqForm }, auth());
      toast.success('تم إرسال طلب التواصل');
      setRequestModal(null); setReqForm({ position_title: '', message: '' });
    } catch (err) { toast.error(errMsg(err, 'فشل إرسال الطلب')); }
  };

  return (
    <div className="space-y-4" data-testid="coach-search-tab">
      <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <div className="relative">
          <Search className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
          <input placeholder="بحث بالاسم" className="w-full rounded-lg border border-slate-200 py-2 pr-9 pl-3 text-sm" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="org-coach-search-input" />
        </div>
        <input placeholder="المدينة" className={inputCls} value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
        <select className={inputCls} value={filters.specialty} onChange={(e) => setFilters({ ...filters, specialty: e.target.value })} data-testid="org-coach-specialty-select">
          <option value="">كل التخصصات</option>
          {Object.entries(COACH_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <p className="text-xs text-slate-400">{total} مدرب مطابق</p>

      {loading ? <p className="text-sm text-slate-400">جارِ التحميل...</p> : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400" data-testid="org-coach-search-empty">لا يوجد مدربون مطابقون لبحثك</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="org-coach-search-card">
              <div className="text-sm font-bold text-slate-800">{c.full_name_ar}</div>
              <div className="text-xs text-slate-500">{COACH_TYPES[c.coach_type] || c.coach_type}</div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                {c.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.city}</span>}
                <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {c.years_experience ?? 0} سنوات</span>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setProfileCoachId(c.id)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal-300" data-testid="org-coach-view-profile-btn">السيرة الذاتية</button>
                <button onClick={() => saveCandidate(c.id)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal-300" data-testid="org-coach-save-candidate-btn">حفظ كمرشح</button>
                <button onClick={() => setRequestModal(c)} className="rounded-lg bg-[#16233b] px-3 py-1.5 text-xs font-semibold text-white" data-testid="org-coach-send-request-btn">إرسال طلب تواصل</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={Boolean(requestModal)} title={`طلب تواصل مع ${requestModal?.full_name_ar || ''}`} onClose={() => setRequestModal(null)}>
        <form onSubmit={sendRequest} className="space-y-3">
          <Field label="المسمى / الدور المطلوب"><input className={inputCls} value={reqForm.position_title} onChange={(e) => setReqForm({ ...reqForm, position_title: e.target.value })} /></Field>
          <Field label="رسالة (اختياري)"><textarea rows={3} className={inputCls} value={reqForm.message} onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })} /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setRequestModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="org-coach-request-submit-btn">إرسال الطلب</PrimaryBtn>
          </div>
        </form>
      </Modal>
      <CoachProfileModal coachId={profileCoachId} onClose={() => setProfileCoachId(null)} />
    </div>
  );
}
